import {combine} from 'effector';
import {createGate} from 'effector-react';

import {
    fetchProductGroups,
    getProductCardLoadStatus,
    loadProductCardFile,
    ProductCardFile,
} from 'api';
import {
    deleteGtin,
    editGtin,
    editGtinVersion,
    getHsnCodes,
    recallGtin,
    releaseNewVersion,
    setApprove,
    setBlock,
    setPublish,
    setRecall,
    setSubmit,
    uploadGtin,
} from 'api/registries/gtins';
import {ProductGroups} from 'core/constants/productGroups';
import {rootDomain} from 'core/rootDomain';
import {GtinUploadDataItem, RecallData} from 'types/gtins';

const domain = rootDomain.createDomain('UploadGtinsDomain');
const POLL_INTERVAL_MS = 1000;

export const ProductsGate = createGate('ProductsGate');

export type CreateProductFormDefaults = {
    product_type: 'GTIN' | 'INTERNAL';
    product_code?: string;
};

export const uploadDataFx = domain.createEffect({
    name: 'uploadDataFx',
    handler: uploadGtin,
});

export const editGtinFx = domain.createEffect<
    {gtin: string | number; productId?: string | null},
    Record<string, any>
>({
    name: 'editGtinFx',
    handler: async ({gtin, productId}) => {
        let data: Record<string, any> = {};
        if (productId) data = await editGtinVersion(productId);
        else if (gtin) data = await editGtin(gtin);

        isFormModalOpenChanged(true);
        return data;
    },
});

export const recallGtinFx = domain.createEffect({
    name: 'recallGtinFx',
    handler: recallGtin,
});

export const deleteGtinFx = domain.createEffect<string, void>({
    name: 'deleteGtinFx',
    handler: deleteGtin,
});

export const getHsnCodesFx = domain.createEffect({
    name: 'getHsnCodesFx',
    handler: getHsnCodes,
});

export const setSubmitFx = domain.createEffect({
    name: 'setSubmitFx',
    handler: setSubmit,
});

export const setBlockFx = domain.createEffect({
    name: 'setBlockFx',
    handler: setBlock,
});

export const setRecallFx = domain.createEffect({
    name: 'setRecallFx',
    handler: setRecall,
});

export const setApproveFx = domain.createEffect({
    name: 'setApproveFx',
    handler: setApprove,
});

export const setPublishFx = domain.createEffect({
    name: 'setPublishFx',
    handler: setPublish,
});

export const releaseNewVersionFx = domain.createEffect({
    name: 'releaseNewVersionFx',
    handler: releaseNewVersion,
});

export const fetchProductGroupsFx = domain.createEffect({
    name: 'fetchProductGroupsFx',
    handler: fetchProductGroups,
});

export const getProductCardLoadStatusFx = domain.createEffect({
    name: 'getProductCardLoadStatusFx',
    handler: getProductCardLoadStatus,
});

export const loadProductCardFileFx = domain.createEffect({
    name: 'loadProductCardFileFx',
    handler: loadProductCardFile,
});

export const pollProductCardLoadStatusDelayFx = domain.createEffect({
    name: 'pollProductCardLoadStatusDelayFx',
    handler: async (taskId: string): Promise<string> => {
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
        return taskId;
    },
});

export const loadProductCardStatusPollingStarted = domain.createEvent<string>();
export const loadProductCardStatusPollingStopped = domain.createEvent();
export const loadProductCardStatusPollingReset = domain.createEvent();

export const loadProductCardTaskId$ = domain.createStore<string | null>(null);
export const loadProductCardTaskIdChanged = domain.createEvent<string | null>();

export const isProductCardLoadStatusPolling$ = domain.createStore(false);

export const loadProductCardFileStatus$ = domain.createStore<ProductCardFile | null>(null);
export const loadProductCardFileStatusChanged = domain.createEvent<ProductCardFile | null>();

export const data$ = domain.createStore<Array<Record<string, any>> | null>(null);
export const dataChanged = domain.createEvent<Array<GtinUploadDataItem> | null>();
export const isModalOpen$ = domain.createStore<boolean>(false);
export const isModalOpenChanged = domain.createEvent<boolean>();
export const dataSubmitted = domain.createEvent();
export const productGroups$ = domain.createStore<Array<{
    code: keyof typeof ProductGroups;
    name: string;
    sampleDescription: string;
}> | null>(null);
export const productGroupsChanged = domain.createEvent<Array<{
    code: keyof typeof ProductGroups;
    name: string;
    sampleDescription: string;
}> | null>();

export const formData$ = domain.createStore<Array<Record<string, any>> | null>(null);
export const formDataChanged = domain.createEvent<Array<Record<string, any>> | null>();
export const createProductFormDefaults$ = domain.createStore<CreateProductFormDefaults | null>(
    null,
);
export const createProductFormDefaultsChanged = domain.createEvent<CreateProductFormDefaults | null>();
export const isFormModalOpen$ = domain.createStore<boolean>(false);
export const isFormModalOpenChanged = domain.createEvent<boolean>();
export const formDataReset = domain.createEvent();
export const isRecallModalOpen$ = domain.createStore<boolean>(false);
export const isRecallModalOpenChanged = domain.createEvent<boolean>();

export const isRecallFailedChanged = domain.createEvent<boolean>();

export const showRecallApproveModal$ = domain.createStore<boolean>(false);
export const showRecallApproveModalChanged = domain.createEvent<boolean>();

export const recallData$ = domain.createStore<RecallData>({
    batchNumberSource: 'byCode',
    batchNumber: '',
    reason: '',
    productCode: '',
    recallDate: null,
    documentNumber: '',
    comment: '',
});
export const recallDataChanged = domain.createEvent<RecallData>();

export const uploadState$ = combine({
    isRecallPending: recallGtinFx.pending,
});
